import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { RefreshButton } from '../shared/RefreshButton';
import { GoogleConnectCard } from '../shared/GoogleConnectCard';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { googleDrive, DriveFile, getDriveFileType } from '../../services/googleDrive';
import { SearchIcon, FileIcon, ClockIcon, ExternalLinkIcon, AlertCircleIcon, XIcon, StarIcon, LinkIcon, Maximize2Icon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0
  }
};
function getInitials(name?: string) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';
}
function getEmbedUrl(file: DriveFile): string {
  if (file.mimeType.includes('document')) {
    return `https://docs.google.com/document/d/${file.id}/preview`;
  }
  if (file.mimeType.includes('spreadsheet')) {
    return `https://docs.google.com/spreadsheets/d/${file.id}/preview`;
  }
  if (file.mimeType.includes('presentation')) {
    return `https://docs.google.com/presentation/d/${file.id}/embed?start=false`;
  }
  if (file.mimeType.includes('form')) {
    return `https://docs.google.com/forms/d/${file.id}/viewform?embedded=true`;
  }
  return file.webViewLink || '';
}
export interface GoogleFilesPanelProps {
  title: string;
  description: string;
  mimeType: string;
  icon: React.ReactNode;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  glowClass: string;
}
export function GoogleFilesPanel({
  title,
  description,
  mimeType,
  icon,
  colorClass,
  bgClass,
  borderClass,
  glowClass
}: GoogleFilesPanelProps) {
  const {
    isConnected,
    isLoading: authLoading,
    signIn
  } = useGoogleAuth();
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<DriveFile | null>(null);
  const fetchFiles = async () => {
    if (!isConnected) return;
    setLoading(true);
    setError(null);
    setDismissedError(false);
    try {
      let data: DriveFile[] = [];
      if (searchQuery.trim()) {
        // When searching, we must filter by mimeType client-side because the search API
        // doesn't easily support complex queries with both fullText and mimeType in our current setup
        const searchResults = await googleDrive.searchFiles(searchQuery);
        data = searchResults.filter(f => f.mimeType === mimeType);
      } else {
        data = await googleDrive.getFilesByMimeType(mimeType);
      }
      setFiles(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isConnected) {
      const timeoutId = setTimeout(() => {
        fetchFiles();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, searchQuery, mimeType]);
  if (authLoading) {
    return <div className="h-full bg-[#020617] flex items-center justify-center" data-id="element-4806">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-4807" />
      </div>;
  }
  if (!isConnected) {
    // Map colorClass to accentColor
    let accentColor: 'red' | 'blue' | 'emerald' | 'amber' | 'purple' = 'blue';
    if (colorClass.includes('emerald')) accentColor = 'emerald';else if (colorClass.includes('amber')) accentColor = 'amber';else if (colorClass.includes('purple')) accentColor = 'purple';else if (colorClass.includes('red')) accentColor = 'red';
    // Map title to highlightService
    let highlightService: any = undefined;
    const t = title.toLowerCase();
    if (t.includes('docs')) highlightService = 'docs';else if (t.includes('sheets')) highlightService = 'sheets';else if (t.includes('slides')) highlightService = 'slides';else if (t.includes('forms')) highlightService = 'forms';
    return <GoogleConnectCard title={title} description={`Conectá tu cuenta de Google para acceder a tus ${title.toLowerCase()} directamente desde el dashboard.`} accentColor={accentColor} highlightService={highlightService} signIn={signIn} error={error} isLoading={authLoading} data-id="element-4808" />;
  }
  return <div className="h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative" data-id="element-4809">
      <div className={`absolute top-0 left-1/4 w-1/2 h-96 ${glowClass} blur-[120px] rounded-full pointer-events-none`} data-id="element-4810" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4811">
        {/* Header */}
        <div className="relative pb-6 mb-8" data-id="element-4812">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4813">
            <div data-id="element-4814">
              <div className="flex items-center gap-3 mb-2" data-id="element-4815">
                <div className={`relative w-12 h-12 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg ${bgClass}`} data-id="element-4816">
                  <div className={`${colorClass} relative z-10`} data-id="element-4817">{icon}</div>
                  <div className={`absolute inset-0 ${bgClass} rounded-xl blur-sm`} data-id="element-4818" />
                </div>
                <div data-id="element-4819">
                  <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-400" data-id="element-4820">
                    {title}
                  </h1>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-4821">{description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-4822">
              <RefreshButton onRefresh={fetchFiles} isRefreshing={loading} data-id="element-4823" />
            </div>
          </div>
          <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${bgClass.replace('/10', '/50')} to-transparent`} data-id="element-4824" />
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-4825">
          {error && !dismissedError && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="mb-8 p-4 bg-red-950/40 border border-red-900/50 rounded-2xl flex items-start gap-3 text-red-400 backdrop-blur-md shadow-lg" data-id="element-4826">
              <AlertCircleIcon size={20} className="shrink-0 mt-0.5" data-id="element-4827" />
              <div className="flex-1" data-id="element-4828">
                <h4 className="text-sm font-bold mb-1 text-red-300" data-id="element-4829">
                  Error de conexión
                </h4>
                <p className="text-sm opacity-90" data-id="element-4830">{error}</p>
              </div>
              <button onClick={() => setDismissedError(true)} className="p-1.5 hover:bg-red-900/50 rounded-lg transition-colors" data-id="element-4831">
                <XIcon size={16} data-id="element-4832" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Search */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-900/40 backdrop-blur-md border border-slate-800/60 p-3 rounded-2xl mb-8 shadow-sm" data-id="element-4833">
          <div className="relative w-full md:max-w-md ml-auto" data-id="element-4834">
            <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4835" />

            <input type="text" placeholder={`Buscar en ${title}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full bg-slate-950/50 border border-slate-800 rounded-xl py-2.5 pl-11 pr-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-slate-600 focus:ring-1 focus:ring-slate-600 transition-all`} data-id="element-4836" />
          </div>
        </div>

        {/* Files Grid */}
        {loading && files.length === 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-id="element-4837">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5 relative overflow-hidden h-40" data-id="element-4838">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-slate-800/20 to-transparent" data-id="element-4839" />
              </div>)}
          </div> : files.length === 0 ? <div className="flex flex-col items-center justify-center py-32 text-center border border-dashed border-slate-800/60 rounded-3xl bg-slate-900/20 backdrop-blur-sm" data-id="element-4840">
            <div className="w-24 h-24 bg-slate-900 rounded-full flex items-center justify-center mb-6 border border-slate-800 shadow-xl relative" data-id="element-4841">
              <div className={`absolute inset-0 ${bgClass} rounded-full blur-md`} data-id="element-4842" />

              <div className={`${colorClass} relative z-10 opacity-50`} data-id="element-4843">
                {icon}
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-200 mb-2" data-id="element-4844">
              No se encontraron archivos
            </h3>
            <p className="text-slate-400 text-sm max-w-sm" data-id="element-4845">
              No hay archivos de este tipo o no coinciden con tu búsqueda.
            </p>
          </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-id="element-4846">
            {files.map(file => {
          const typeInfo = getDriveFileType(file.mimeType);
          const modifiedDate = new Date(file.modifiedTime).toLocaleDateString('es-AR', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          const owner = file.owners?.[0]?.displayName || 'Desconocido';
          return <motion.div variants={itemVariants} key={file.id} className={`bg-slate-900/60 backdrop-blur-md border border-slate-800/60 rounded-2xl overflow-hidden hover:${borderClass.replace('/20', '/40')} transition-all duration-300 group shadow-sm flex flex-col h-full relative cursor-pointer`} onClick={() => setSelectedFile(file)} data-id="element-4847">
                  {/* Hover Actions Overlay */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex gap-1" data-id="element-4848">
                    <button onClick={e => {
                e.stopPropagation();
                navigator.clipboard.writeText(file.webViewLink || '');
              }} className="p-1.5 bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg backdrop-blur-sm transition-colors" title="Copiar enlace" data-id="element-4849">
                      <LinkIcon size={14} data-id="element-4850" />
                    </button>
                    <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className={`p-1.5 ${bgClass.replace('/10', '/90')} hover:${bgClass.replace('/10', '')} ${colorClass} rounded-lg backdrop-blur-sm transition-colors`} title="Abrir en Google" data-id="element-4851">
                      <ExternalLinkIcon size={14} data-id="element-4852" />
                    </a>
                  </div>

                  {/* Card Content */}
                  <div className="flex flex-col h-full p-5" data-id="element-4853">
                    <div className="flex items-start gap-4 mb-4" data-id="element-4854">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border ${bgClass} ${colorClass} ${borderClass} shadow-inner`} data-id="element-4855">
                        {icon}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5" data-id="element-4856">
                        <h3 className={`text-sm font-bold text-slate-200 group-hover:${colorClass} transition-colors truncate leading-tight mb-1`} title={file.name} data-id="element-4857">
                          {file.name}
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-4858">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-slate-700/50 bg-slate-950/50 ${colorClass}`} data-id="element-4859">
                            {typeInfo.label}
                          </Badge>
                          {file.starred && <StarIcon size={12} className="text-amber-400 fill-amber-400" data-id="element-4860" />}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto pt-4 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-400" data-id="element-4861">
                      <div className="flex items-center gap-2 truncate max-w-[60%]" data-id="element-4862">
                        <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[9px] font-bold text-slate-300 shrink-0" data-id="element-4863">
                          {getInitials(owner)}
                        </div>
                        <span className="truncate" data-id="element-4864">{owner}</span>
                      </div>
                      <span className="flex items-center gap-1.5 shrink-0 font-mono" data-id="element-4865">
                        <ClockIcon size={12} data-id="element-4866" /> {modifiedDate}
                      </span>
                    </div>
                  </div>
                </motion.div>;
        })}
          </motion.div>}
      </div>

      {/* Preview Overlay */}
      <AnimatePresence data-id="element-4867">
        {selectedFile && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-12 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedFile(null)} data-id="element-4868">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full h-full max-w-7xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-4869">
              {/* Overlay Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/50" data-id="element-4870">
                <div className="flex items-center gap-3 min-w-0" data-id="element-4871">
                  <div className={`p-1.5 rounded-lg ${bgClass} ${colorClass}`} data-id="element-4872">
                    {icon}
                  </div>
                  <h2 className="text-sm font-bold text-slate-200 truncate pr-4" data-id="element-4873">
                    {selectedFile.name}
                  </h2>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-4874">
                  <a href={selectedFile.webViewLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-4875">
                    <ExternalLinkIcon size={14} data-id="element-4876" />
                    <span className="hidden sm:inline" data-id="element-4877">Abrir en Google</span>
                  </a>
                  <div className="w-px h-4 bg-slate-700 mx-1" data-id="element-4878" />
                  <button onClick={() => setSelectedFile(null)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-4879">
                    <XIcon size={18} data-id="element-4880" />
                  </button>
                </div>
              </div>

              {/* Iframe Container */}
              <div className="flex-1 bg-white relative" data-id="element-4881">
                {/* Loading state behind iframe */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-slate-400" data-id="element-4882">
                  <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" data-id="element-4883" />
                  <p className="text-sm" data-id="element-4884">Cargando vista previa...</p>
                  <p className="text-xs text-slate-500 mt-2 max-w-sm text-center" data-id="element-4885">
                    Si el documento no carga, verificá que tengas sesión
                    iniciada en Google en este navegador o abrilo en una nueva
                    pestaña.
                  </p>
                </div>

                <iframe src={getEmbedUrl(selectedFile)} className="absolute inset-0 w-full h-full border-0 z-10 bg-white" title={selectedFile.name} allow="autoplay; camera; microphone; fullscreen; display-capture" sandbox="allow-same-origin allow-scripts allow-popups allow-forms" data-id="element-4886" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Helper to map mimeType to service ID for GoogleConnectCard
function getServiceFromMimeType(mimeType: string): 'docs' | 'sheets' | 'slides' | 'forms' | 'drive' {
  if (mimeType.includes('document')) return 'docs';
  if (mimeType.includes('spreadsheet')) return 'sheets';
  if (mimeType.includes('presentation')) return 'slides';
  if (mimeType.includes('form')) return 'forms';
  return 'drive';
}
// Helper to map colorClass to accent color
function getAccentFromColorClass(colorClass: string): 'blue' | 'emerald' | 'amber' | 'purple' | 'red' {
  if (colorClass.includes('blue')) return 'blue';
  if (colorClass.includes('emerald')) return 'emerald';
  if (colorClass.includes('amber')) return 'amber';
  if (colorClass.includes('purple')) return 'purple';
  if (colorClass.includes('red')) return 'red';
  return 'blue';
}